import { DocumentSearchResult } from '@dc-shared-web/hooks/api/document'
import { Folder } from '@dc-shared-web/hooks/api/folder'
import { Project } from '@dc-shared-web/hooks/api/project'
import { SolutionType } from '@dc-shared-web/hooks/api/solutionType'

import shortenUUID from '@dc-shared-web/lib/shortenUUID'
import { SortingState } from '@tanstack/react-table'

export type CubeDocumentSearchResult = {
  'Crunch.created_at': string
  'Crunch.created_at_date': string
  'Crunch.crunched_by': string
  'Crunch.document_guid': string
  'Crunch.filename': string
  'Crunch.folder_id': number
  'Crunch.folder_name': string
  'Crunch.folder_uuid': string
  'Crunch.is_archived': boolean
  'Crunch.is_shared': boolean
  'Crunch.job_status': string // is there enum vals?
  'Crunch.project_id': number
  'Crunch.project_name': string
  'Crunch.project_uuid': string
  'Crunch.share_count': number
  'Crunch.shared_with': string
  'Crunch.status': string // is there enum vals?
  'Crunch.team_id': number
  'Crunch.team_uuid': string
  'Crunch.team_name': string
  'Crunch.type': string // is there enum vals?
  'Crunch.user': string
  'Crunch.uuid': string
  'Crunch.versions': string[]
  'Crunch.document_type_name': string
}

export const mapCubeToDocument = (
  data: CubeDocumentSearchResult
): DocumentSearchResult => {
  return {
    // Append 'Z' to indicate UTC timezone since the API returns ISO timestamp without timezone
    createdAt: new Date(`${data['Crunch.created_at']}Z`),
    createdAtDate: new Date(`${data['Crunch.created_at_date']}Z`),
    crunchedBy: data['Crunch.crunched_by'],
    documentGuid: data['Crunch.document_guid'],
    fileName: data['Crunch.filename'],
    folderId: data['Crunch.folder_id'],
    folderUuid: data['Crunch.folder_uuid'],
    folderName: data['Crunch.folder_name'],
    isArchived: data['Crunch.is_archived'],
    isShared: data['Crunch.is_shared'],
    jobStatus: data['Crunch.job_status'],
    projectId: data['Crunch.project_id'],
    projectUuid: data['Crunch.project_uuid'],
    projectName: data['Crunch.project_name'],
    shareCount: data['Crunch.share_count'],
    sharedWith: data['Crunch.shared_with'],
    status: data['Crunch.status'] as DocumentSearchResult['status'],
    teamId: data['Crunch.team_id'],
    teamUUID: data['Crunch.team_uuid'],
    teamName: data['Crunch.team_name'],
    type: data['Crunch.type'],
    user: data['Crunch.user'],
    uuid: data['Crunch.uuid'],
    _shortenedUuid: shortenUUID(data['Crunch.uuid']),
    versions: data['Crunch.versions'],
    documentTypeName: data['Crunch.document_type_name'],
  }
}

const mappedKeys = {
  createdAt: 'Crunch.created_at',
  createdAtDate: 'Crunch.created_at_date',
  crunchedBy: 'Crunch.crunched_by',
  fileName: 'Crunch.filename',
  folderId: 'Crunch.folder_id',
  folderName: 'Crunch.folder_name',
  isArchived: 'Crunch.is_archived',
  isShared: 'Crunch.is_shared',
  jobStatus: 'Crunch.job_status',
  projectId: 'Crunch.project_id',
  projectName: 'Crunch.project_name',
  shareCount: 'Crunch.share_count',
  sharedWith: 'Crunch.shared_with',
  status: 'Crunch.status',
  teamId: 'Crunch.team_id',
  teamUUID: 'Crunch.team_uuid',
  teamName: 'Crunch.team_name',
  type: 'Crunch.type',
  user: 'Crunch.user',
  uuid: 'Crunch.uuid',
}

export const getCubeDocumentProp = (documentProp: keyof typeof mappedKeys) => {
  return mappedKeys[documentProp]
}

export const getCubeDocumentPropFromSorting = (
  sorting: SortingState
): string | undefined => {
  return getCubeDocumentProp(sorting[0]?.id as keyof typeof mappedKeys)
}

export type CubeSolutionType = {
  'Crunch.type': string
}

export const mapCubeToSolutionType = (data: CubeSolutionType): SolutionType => {
  return {
    type: data['Crunch.type'],
  }
}

export type CubeFolder = {
  'Folder.child_count': number
  'Folder.children': unknown // what should this be?
  'Folder.crunch_count': number | null
  'Folder.crunches': string
  'Folder.id': number
  'Folder.level': number
  'Folder.name': string
  'Folder.parent_id': number
  'Folder.project_count': number | null
  'Folder.projects': string
  'Folder.uuid': string
}

export const mapCubeToFolder = (data: CubeFolder): Folder => {
  return {
    childCount: data['Folder.child_count'],
    children: data['Folder.children'],
    crunchCount: data['Folder.crunch_count'],
    crunches: data['Folder.crunches'],
    id: data['Folder.id'],
    level: data['Folder.level'],
    name: data['Folder.name'],
    parentId: data['Folder.parent_id'],
    projectCount: data['Folder.project_count'],
    projects: data['Folder.projects'],
    uuid: data['Folder.uuid'],
  }
}

export type CubeProject = {
  'Project.id': number
  'Project.name': string
  'Project.crunch_count': number | null
  'Project.uuid': string
  'Project.identifier': string
  'Project.folder_id': number
  'Project.team_id': number
}

export const mapCubeToProject = (data: CubeProject): Project => {
  return {
    id: data['Project.id'],
    name: data['Project.name'],
    crunchCount: data['Project.crunch_count'],
    uuid: data['Project.uuid'],
    identifier: data['Project.identifier'],
    folderId: data['Project.folder_id'],
    teamId: data['Project.team_id'],
    //there are required project fields that are not mapped above, hence the type casting.
  } as Project
}

export interface ProcessingCrunchEntry {
  'Crunch.uuid': string
  'Crunch.filename': string
  'Crunch.user': string
  'Crunch.created_at': string
  'Crunch.type': string
  'Crunch.project_name': string | null
  'Crunch.crunched_by': string
  'Crunch.project_id': number | null
  'Crunch.team_id': number
  'Crunch.team_uuid': string
  'Crunch.team_name': string
  'Crunch.is_shared': number | null
  'Crunch.share_count': number | null
  'Crunch.shared_with': string | null
  'Crunch.is_archived': number
  'Crunch.status': 'In Progress' | 'completed' | 'error'
  'Crunch.gen_checklist_status'?: 'In Progress' | 'completed' | 'error'
  'Crunch.gen_checklist': Record<string, 'In Progress' | 'completed' | 'error'>
  'Crunch.gen_checklist_questions': Record<
    string,
    'In Progress' | 'completed' | 'error'
  >
  'Crunch.job_status': string
  'Crunch.hq_pipeline': number
  'Crunch.document_guid': string
  'Crunch.created_at_day': string
}

export interface ProcessingDocument {
  uuid: string
  filename: string
  status: 'In Progress' | 'completed' | 'error'
  genChecklistStatus?: 'In Progress' | 'completed' | 'error'
  genAIChecklists: Record<string, 'In Progress' | 'completed' | 'error'>
  genAIChecklistQuestions: Record<string, 'In Progress' | 'completed' | 'error'>
}

export const mapCubeToProcessingDocument = (
  entry: ProcessingCrunchEntry
): ProcessingDocument => {
  return {
    uuid: entry['Crunch.uuid'],
    filename: entry['Crunch.filename'],
    status: entry['Crunch.status'],
    genChecklistStatus: entry['Crunch.gen_checklist_status'],
    genAIChecklists: entry['Crunch.gen_checklist'],
    genAIChecklistQuestions: entry['Crunch.gen_checklist_questions'],
  }
}
